/**
 * Admin Users Page for trAInedup.ai
 * Comprehensive user management interface for administrators
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Search,
  Filter,
  Download,
  Upload,
  Plus,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  RefreshCw,
  X,
  Calendar,
  CreditCard,
  BookOpen,
  Trash2,
  Settings,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { adminService } from '../services/firebase/admin';
import { AdminUserProfile, UserStatus } from '../types/admin';
import { ProgressService, CourseService } from '../services/firebase/firestore';
import { getAffiliateProfile } from '../services/firebase/affiliateProfile';
import { UserProgress, Course } from '../types';

/**
 * Format date helper
 */
const formatDate = (date?: Date | any) => {
  if (!date) return 'Never';
  // Handle Firebase Timestamp
  const d = date.toDate ? date.toDate() : new Date(date);
  
  // Check if date is valid
  if (isNaN(d.getTime())) return 'Invalid Date';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

// --- User Details Modal Component ---
interface UserDetailsModalProps {
  userId: string;
  initialTab?: 'profile' | 'subscription' | 'learning' | 'billing';
  onClose: () => void;
  onUpdate: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ userId, initialTab = 'profile', onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'subscription' | 'learning' | 'billing'>(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  // Form states
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<UserStatus>(UserStatus.ACTIVE);
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string>('');

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const details = await adminService.getUserDetails(userId);
        setUserDetails(details);
        
        // Initialize form states
        if (details.user) {
          setSelectedRole(details.user.roles?.[0] || 'student');
          setSelectedStatus(details.user.status);
          setSelectedTier(details.user.subscriptionTier || 'free');
          if (details.user.subscriptionEndDate) {
            setSubscriptionEndDate(new Date(details.user.subscriptionEndDate).toISOString().split('T')[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        setError('Failed to load user details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchDetails();
    }
  }, [userId]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      // Update roles
      await adminService.updateUserRoles(userId, [selectedRole]);
      // Update status
      await adminService.updateUserStatus(userId, selectedStatus);
      
      alert('Profile updated successfully');
      onUpdate();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      setSaving(true);
      const endDate = subscriptionEndDate ? new Date(subscriptionEndDate) : undefined;
      await adminService.updateUserSubscription(userId, selectedTier, endDate);
      alert('Subscription updated successfully');
      onUpdate();
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        setSaving(true);
        await adminService.deleteUser(userId);
        onUpdate();
        onClose();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user');
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">Error Loading User</h3>
          <p className="text-neutral-600 mb-6">{error}</p>
          <Button onClick={onClose} variant="primary" className="w-full">
            Close
          </Button>
        </div>
      </div>
    );
  }

  if (!userDetails) return null;

  const { user, learningHistory, billingHistory } = userDetails;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between bg-neutral-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-xl">
              {user.displayName?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-neutral-900">{user.displayName}</h2>
              <p className="text-sm text-neutral-500">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200">
          {[
            { id: 'profile', label: 'Profile', icon: Users },
            { id: 'subscription', label: 'Subscription', icon: CreditCard },
            { id: 'learning', label: 'Learning History', icon: BookOpen },
            { id: 'billing', label: 'Billing', icon: Calendar },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary bg-primary/5'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Display Name</label>
                  <Input value={user.displayName || ''} disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
                  <Input value={user.email || ''} disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Date Joined</label>
                  <Input value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'} disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Last Active</label>
                  <Input value={user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleDateString() : 'N/A'} disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Email Verified</label>
                  <div className="px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-700">
                    {user.emailVerified ? 'Yes' : 'No'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Affiliate</label>
                  <div className="px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-700">
                    {user.isAffiliate ? 'Yes' : 'No'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Role</label>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value as UserStatus)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {Object.values(UserStatus).map((status) => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pt-6 border-t border-neutral-200 flex justify-between">
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleDeleteUser} disabled={saving}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete User
                </Button>
                <Button variant="primary" onClick={handleSaveProfile} loading={saving}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-900 mb-2">Current Subscription</h3>
                <div className="flex items-center gap-4">
                  <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-blue-700 shadow-sm">
                    {user.subscriptionTier?.toUpperCase() || 'FREE'}
                  </span>
                  <span className="text-sm text-blue-700">
                    {user.subscriptionEndDate 
                      ? `Expires on ${new Date(user.subscriptionEndDate).toLocaleDateString()}`
                      : 'No expiration date'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Change Tier</label>
                  <select
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="free">Free</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="corporate">Corporate</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Expiration Date (Optional)</label>
                  <input
                    type="date"
                    value={subscriptionEndDate}
                    onChange={(e) => setSubscriptionEndDate(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <p className="text-xs text-neutral-500 mt-1">Leave blank for indefinite subscription</p>
                </div>
              </div>

              <div className="pt-6 border-t border-neutral-200 flex justify-end">
                <Button variant="primary" onClick={handleUpdateSubscription} loading={saving}>
                  Update Subscription
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'learning' && (
            <div className="space-y-4">
              <h3 className="font-medium text-neutral-900">Enrolled Courses</h3>
              {learningHistory && learningHistory.length > 0 ? (
                <div className="space-y-3">
                  {learningHistory.map((course: any) => {
                    const isCompleted = course.status === 'completed' || !!course.completedAt;
                    return (
                    <div key={course.id} className="p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-medium text-neutral-900">{course.courseTitle || course.courseId}</h4>
                          <p className="text-sm text-neutral-500">
                            Started: {formatDate(course.startedAt || course.lastAccessedAt)}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isCompleted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {isCompleted ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                      <div className="w-full bg-neutral-200 rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full" 
                          style={{ width: `${course.completionPercentage || 0}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-neutral-500">{course.completionPercentage || 0}% Complete</span>
                        {course.completedAt && (
                          <span className="text-xs text-neutral-500">
                            Finished: {formatDate(course.completedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              ) : (
                <p className="text-neutral-500 italic">No learning history found.</p>
              )}
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-4">
              <h3 className="font-medium text-neutral-900">Billing History</h3>
              {billingHistory && billingHistory.length > 0 ? (
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Plan</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {billingHistory.map((invoice: any) => (
                      <tr key={invoice.id}>
                        <td className="px-4 py-2 text-sm text-neutral-900">
                          {new Date(invoice.date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 text-sm text-neutral-900">{invoice.plan}</td>
                        <td className="px-4 py-2 text-sm text-neutral-900">
                          £{(invoice.amount / 100).toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {invoice.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-neutral-500 italic">No billing history found.</p>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

/**
 * Admin users page component
 * Provides comprehensive user management functionality
 */
export const AdminUsersPage: React.FC = () => {
  
  
  // State
  const [users, setUsers] = useState<AdminUserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [roleFilter, setRoleFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'createdAt' | 'lastActiveAt' | 'displayName'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [resettingCooldown, setResettingCooldown] = useState<string | null>(null);
  
  // Modal State
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserTab, setSelectedUserTab] = useState<'profile' | 'subscription' | 'learning' | 'billing'>('profile');
  
  // Progress State
  const [userProgressMap, setUserProgressMap] = useState<Record<string, UserProgress[]>>({});
  const [selectedUserProgress, setSelectedUserProgress] = useState<{user: AdminUserProfile, type: 'in-progress' | 'completed', progress: UserProgress[]} | null>(null);
  const [courseCache, setCourseCache] = useState<Record<string, Course>>({});


  /**
   * Reset skills assessment cooldown for a user
   */
  const handleResetAssessmentCooldown = async (userId: string) => {
    try {
      setResettingCooldown(userId);
      await adminService.resetAssessmentCooldown(userId);
      alert('Assessment cooldown reset successfully!');
      loadUsers(); // Reload to show updated data
    } catch (error) {
      console.error('Error resetting assessment cooldown:', error);
      alert('Failed to reset assessment cooldown');
    } finally {
      setResettingCooldown(null);
    }
  };

  /**
 * Load users with filters
 */
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { users: usersData } = await adminService.getUsers({
        status: statusFilter || undefined,
        role: roleFilter || undefined,
        subscriptionTier: subscriptionFilter || undefined,
        searchTerm: searchTerm || undefined,
        limit: 100,
      });
      setUsers(usersData);

      // Fetch progress for all users
      const progressPromises = usersData.map(user => ProgressService.getUserAllProgress(user.id));
      const affiliatePromises = usersData.map(user => getAffiliateProfile(user.id));
      
      const [progressResults, affiliateResults] = await Promise.all([
        Promise.all(progressPromises),
        Promise.all(affiliatePromises)
      ]);
      
      const newProgressMap: Record<string, UserProgress[]> = {};
      
      // Update users with affiliate status
      const updatedUsers = usersData.map((user, index) => {
        newProgressMap[user.id] = progressResults[index];
        return {
          ...user,
          isAffiliate: !!affiliateResults[index]
        };
      });
      
      setUsers(updatedUsers);
      setUserProgressMap(newProgressMap);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter, subscriptionFilter, searchTerm]);

  /**
   * Load users
   */
  useEffect(() => {
    loadUsers();
  }, [loadUsers, statusFilter, roleFilter, subscriptionFilter, sortBy, sortOrder]);



  /**
   * Handle search
   */
  const handleSearch = () => {
    loadUsers();
  };



  /**
   * Get status badge
   */
  const getStatusBadge = (status: UserStatus) => {
    const statusConfig = {
      [UserStatus.ACTIVE]: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      [UserStatus.INACTIVE]: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      [UserStatus.SUSPENDED]: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      [UserStatus.BANNED]: { color: 'bg-red-100 text-red-800', icon: XCircle },
      [UserStatus.PENDING_VERIFICATION]: { color: 'bg-blue-100 text-blue-800', icon: Clock },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ').toLowerCase()}
      </span>
    );
  };

  /**
   * Get subscription badge
   */
  const getSubscriptionBadge = (tier: string) => {
    const tierConfig = {
      free: { color: 'bg-gray-100 text-gray-800' },
      standard: { color: 'bg-blue-100 text-blue-800' },
      premium: { color: 'bg-purple-100 text-purple-800' },
      corporate: { color: 'bg-gold-100 text-gold-800' },
    };

    const config = tierConfig[tier as keyof typeof tierConfig] || tierConfig.free;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {tier}
      </span>
    );
  };

  /**
   * Handle showing user progress details
   */
  const handleShowProgress = async (user: AdminUserProfile, type: 'in-progress' | 'completed') => {
    const progressList = userProgressMap[user.id] || [];
    const filteredProgress = progressList.filter(p => {
      const isCompleted = p.completionPercentage === 100 || !!p.completedAt;
      return type === 'completed' ? isCompleted : !isCompleted;
    });

    // Fetch missing course details
    const missingCourseIds = filteredProgress
      .map(p => p.courseId)
      .filter(id => !courseCache[id]);
    
    if (missingCourseIds.length > 0) {
      try {
        const coursePromises = missingCourseIds.map(id => CourseService.getCourse(id));
        const courses = await Promise.all(coursePromises);
        
        setCourseCache(prev => {
          const newCache = { ...prev };
          courses.forEach(c => {
            if (c) newCache[c.id] = c;
          });
          return newCache;
        });
      } catch (error) {
        console.error('Error fetching course details:', error);
      }
    }

    setSelectedUserProgress({
      user,
      type,
      progress: filteredProgress
    });
  };

  const getProgressCounts = (userId: string) => {
    const progressList = userProgressMap[userId] || [];
    const completed = progressList.filter(p => p.completionPercentage === 100 || !!p.completedAt).length;
    const inProgress = progressList.length - completed;
    return { completed, inProgress };
  };

  /**
   * User row component
   */
  const UserRow: React.FC<{ user: AdminUserProfile }> = ({ user }) => {
    return (
      <tr className="hover:bg-neutral-50">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-neutral-200 rounded-full flex items-center justify-center mr-3">
              {user.avatar ? (
                <img src={user.avatar} alt={user.displayName} className="w-10 h-10 rounded-full" />
              ) : (
                <span className="text-sm font-medium text-neutral-600">
                  {user.displayName.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <div className="text-sm font-medium text-neutral-900">{user.displayName}</div>
              <div className="text-sm text-neutral-500">{user.email}</div>
            </div>
          </div>
        </td>


        <td className="px-6 py-4 whitespace-nowrap">
          {user.emailVerified ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" /> Verified
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <XCircle className="w-3 h-3 mr-1" /> Pending
            </span>
          )}
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          {user.isAffiliate ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              <CheckCircle className="w-3 h-3 mr-1" /> Yes
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
              No
            </span>
          )}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap">
          {getStatusBadge(user.status)}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap">
          {getSubscriptionBadge(user.subscriptionTier)}
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              {user.skillsAssessmentCompleted ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              <span className="text-sm text-neutral-600">
                {user.skillsAssessmentCompleted ? 'Yes' : 'No'}
              </span>
            </div>
            {user.skillsAssessmentCompleted && user.skillsAssessmentCompletedAt && (
              <span className="text-xs text-neutral-500 mt-1">
                {formatDate(user.skillsAssessmentCompletedAt)}
              </span>
            )}
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <button 
            onClick={() => handleShowProgress(user, 'in-progress')}
            className="text-primary hover:underline font-medium"
          >
            {getProgressCounts(user.id).inProgress}
          </button>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <button 
            onClick={() => handleShowProgress(user, 'completed')}
            className="text-primary hover:underline font-medium"
          >
            {getProgressCounts(user.id).completed}
          </button>
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
          {user.roles.join(', ') || 'User'}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
          {formatDate(user.lastActiveAt)}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
          {formatDate(user.createdAt)}
        </td>
        
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              icon={Settings}
              onClick={() => {
                setSelectedUserTab('learning');
                setSelectedUserId(user.id);
              }}
              title="Manage User Details"
              className="text-neutral-500 hover:text-primary hover:bg-primary/10"
            />
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              icon={RefreshCw}
              onClick={() => handleResetAssessmentCooldown(user.id)}
              disabled={resettingCooldown === user.id}
              title="Reset Skills Assessment"
              className="text-neutral-500 hover:text-primary hover:bg-primary/10"
            />
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="py-8">
      {selectedUserId && (
        <UserDetailsModal
          userId={selectedUserId}
          initialTab={selectedUserTab}
          onClose={() => setSelectedUserId(null)}
          onUpdate={loadUsers}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="font-montserrat font-bold text-3xl text-neutral-800 mb-2">
                User Management
              </h1>
              <p className="text-lg text-neutral-600">
                Manage user accounts, permissions, and activity
              </p>
            </div>
            
            <div className="mt-6 lg:mt-0 flex items-center space-x-4">
              <Button variant="outline" icon={Upload}>
                Import
              </Button>
              <Button variant="outline" icon={Download}>
                Export
              </Button>
              <Button variant="primary" icon={Plus}>
                Add User
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">{users.length}</p>
                  <p className="text-sm text-neutral-600">Total Users</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">
                    {users.filter(u => u.status === UserStatus.ACTIVE).length}
                  </p>
                  <p className="text-sm text-neutral-600">Active Users</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <Star className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">
                    {users.filter(u => u.subscriptionTier !== 'free').length}
                  </p>
                  <p className="text-sm text-neutral-600">Paid Users</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">
                    {users.filter(u => u.status === UserStatus.SUSPENDED || u.status === UserStatus.BANNED).length}
                  </p>
                  <p className="text-sm text-neutral-600">Suspended/Banned</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Filters and Search */}
          <Card className="p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex-1 max-w-md">
                <Input
                  type="text"
                  placeholder="Search users by name, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  icon={Search}
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <Button
                  variant="outline"
                  icon={Filter}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters
                </Button>
                
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as UserStatus)}
                  className="border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">All Status</option>
                  {Object.values(UserStatus).map(status => (
                    <option key={status} value={status}>
                      {status.replace('_', ' ')}
                    </option>
                  ))}
                </select>
                
                <select
                  value={subscriptionFilter}
                  onChange={(e) => setSubscriptionFilter(e.target.value)}
                  className="border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">All Subscriptions</option>
                  <option value="free">Free</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="corporate">Corporate</option>
                </select>
              </div>
            </div>
            
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-neutral-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Role
                    </label>
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="instructor">Instructor</option>
                      <option value="student">Student</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as any)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="createdAt">Created Date</option>
                      <option value="lastActiveAt">Last Active</option>
                      <option value="displayName">Name</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Order
                    </label>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as any)}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="desc">Newest First</option>
                      <option value="asc">Oldest First</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Users Table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Verified
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Affiliate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Skills Assessment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      In Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Last Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Settings
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Reset Assessment
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {loading ? (
                    <tr>
                      <td colSpan={15} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      </td>
                    </tr>
                  ) : users.length > 0 ? (
                    users.map(user => (
                      <UserRow key={user.id} user={user} />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={15} className="px-6 py-12 text-center">
                        <div className="text-center">
                          <Users className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                          <h3 className="font-medium text-neutral-900 mb-2">No users found</h3>
                          <p className="text-neutral-600">
                            Try adjusting your search criteria or filters.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

      {/* Progress Details Modal */}
      {selectedUserProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-neutral-200 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-neutral-800">
                  {selectedUserProgress.type === 'in-progress' ? 'Courses In Progress' : 'Completed Courses'}
                </h2>
                <p className="text-neutral-600">
                  {selectedUserProgress.user.displayName}
                </p>
              </div>
              <button 
                onClick={() => setSelectedUserProgress(null)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              {selectedUserProgress.progress.length === 0 ? (
                <p className="text-center text-neutral-500 py-8">
                  No courses {selectedUserProgress.type === 'in-progress' ? 'in progress' : 'completed'}.
                </p>
              ) : (
                <div className="space-y-4">
                  {selectedUserProgress.progress.map((progress) => {
                    const course = courseCache[progress.courseId];
                    return (
                      <div key={progress.courseId} className="border border-neutral-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium text-neutral-800">
                            {course?.title || 'Loading course details...'}
                          </h3>
                          <span className="text-xs font-medium px-2 py-1 bg-neutral-100 rounded-full text-neutral-600">
                            {progress.completionPercentage}%
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-neutral-600 mt-3">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-2 text-neutral-400" />
                            <span>Started: {formatDate(progress.startedAt || progress.lastAccessedAt)}</span>
                          </div>
                          {progress.completedAt && (
                            <div className="flex items-center">
                              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                              <span>Completed: {formatDate(progress.completedAt)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-neutral-200 bg-neutral-50 flex justify-end">
              <Button onClick={() => setSelectedUserProgress(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

